import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, X, ArrowRight, Command } from "lucide-react";

/**
 * Cmd+K / Ctrl+K global command palette. Debounced search across
 * projects, deals, contacts, documents, and portal users.
 */
export default function CommandPalette({ open, onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState({});
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 50);
        if (!open) { setQ(""); setResults({}); }
    }, [open]);

    useEffect(() => {
        if (!open || q.trim().length < 2) { setResults({}); return; }
        setBusy(true);
        const h = setTimeout(async () => {
            try {
                const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
                setResults(data);
            } catch { setResults({}); }
            setBusy(false);
        }, 200);
        return () => clearTimeout(h);
    }, [q, open]);

    if (!open) return null;

    const go = (path) => { onClose(); nav(path); };

    const sections = [
        { key: "projects", label: "Projects", items: results.projects || [], render: (i) => ({ title: i.name, subtitle: `${i.city || ""} · ${i.status || ""}`, path: `/app/projects/${i.id}` }) },
        { key: "deals", label: "Deals", items: results.deals || [], render: (i) => ({ title: i.name || i.deal_type, subtitle: i.status, path: `/app/deals/${i.id}` }) },
        { key: "contacts", label: "Contacts", items: results.contacts || [], render: (i) => ({ title: `${i.first_name || ""} ${i.last_name || ""}`.trim() || i.email, subtitle: i.email || i.company, path: `/app/contacts/${i.id}` }) },
        { key: "documents", label: "Documents", items: results.documents || [], render: (i) => ({ title: i.filename, subtitle: i.category, path: `/app/projects/${i.project_id}` }) },
        { key: "portal_users", label: "Portal users", items: results.portal_users || [], render: (i) => ({ title: i.email, subtitle: `${i.portal_type} portal`, path: `/app/admin/portal-users` }) },
    ];

    const hasResults = sections.some((s) => s.items.length > 0);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 bg-black/40" onClick={onClose} data-testid="command-palette">
            <div className="w-full max-w-2xl bg-background border border-border shadow-lg" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-border flex items-center gap-2 px-3 py-2">
                    <Search size={14} className="text-muted-foreground" />
                    <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, deals, contacts, docs…" className="flex-1 bg-transparent outline-none text-sm py-1" data-testid="command-palette-input" />
                    <kbd className="text-[10px] text-muted-foreground border border-border px-1.5 py-0.5">Esc</kbd>
                    <button onClick={onClose} className="p-1 hover:bg-secondary"><X size={12} /></button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {q.trim().length < 2 ? (
                        <div className="p-6 text-xs text-muted-foreground text-center">Type at least 2 characters to search…</div>
                    ) : busy ? (
                        <div className="p-6 text-xs text-muted-foreground text-center">Searching…</div>
                    ) : !hasResults ? (
                        <div className="p-6 text-xs text-muted-foreground text-center" data-testid="command-palette-empty">No matches for &ldquo;{q}&rdquo;</div>
                    ) : (
                        sections.map((s) => s.items.length > 0 && (
                            <div key={s.key} className="border-b border-border last:border-b-0" data-testid={`palette-section-${s.key}`}>
                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/30">{s.label}</div>
                                {s.items.map((i) => {
                                    const { title, subtitle, path } = s.render(i);
                                    return (
                                        <button key={i.id} onClick={() => go(path)} className="w-full text-left px-3 py-2 hover:bg-secondary/60 flex items-center gap-2 text-sm" data-testid={`palette-result-${i.id}`}>
                                            <span className="flex-1 truncate">{title}</span>
                                            <span className="text-xs text-muted-foreground truncate">{subtitle}</span>
                                            <ArrowRight size={12} className="text-muted-foreground" />
                                        </button>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>
                <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground flex items-center gap-3">
                    <span className="flex items-center gap-1"><Command size={9} />K to open · Enter to select</span>
                </div>
            </div>
        </div>
    );
}
